import axios from 'axios';
import { useConfigHelper } from '@ilteducation/config';
import { getShareTarget, setShareTarget } from './analytics-gateway-api';

/*
 One report per reading session, per tab. A reading session is minted on every BookPlayer mount, so
 without the guard a remount inside the same session would report the start twice.
*/
const REPORTED_SESSIONS_KEY = 'reported-share-sessions';

const isNewSession = (readingSessionId: string): boolean => {
  try {
    const reported: string[] = JSON.parse(sessionStorage.getItem(REPORTED_SESSIONS_KEY) ?? '[]');

    if (reported.includes(readingSessionId)) {
      return false;
    }

    sessionStorage.setItem(REPORTED_SESSIONS_KEY, JSON.stringify([...reported, readingSessionId]));
  } catch {
    // Private mode and friends: a duplicate report beats no report at all.
  }

  return true;
};

/**
 * Tells shared-content that listening started on the share this tab was opened from, and does nothing
 * at all outside a share.
 *
 * The open is known by its openId and the player events by their reading session; this event carries
 * both, so `book_player_events.reading_session_id` joins straight to the open.
 *
 * Call it once per reading session, right after the session id is minted.
 */
export const reportShareListenStarted = (readingSessionId: string, contentId: string) => {
  const target = getShareTarget();

  if (!target || target.contentId !== contentId || !isNewSession(readingSessionId)) {
    return;
  }

  /*
   A signed-in reader needs the target for this one report only. Kept, it would count any later reading of the same
   book in this tab as listening to the share. A guest keeps it: their player events go through the share.
   */
  if (!target.asGuest) {
    setShareTarget(null);
  }

  const { getApiUrl } = useConfigHelper();
  const route = target.asGuest ? `public/${target.shareId}` : target.shareId;

  axios
    .post(
      `${getApiUrl('shared-contents')}/${route}/events`,
      { eventName: 'share_listen_started', openId: target.openId, readingSessionId },
      { noAuth: target.asGuest },
    )
    .catch((error) => console.error('Failed to report that listening to a share started', error));
};
